import React, { useEffect, useRef, useState } from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import GeneralEditPanelBase from '../../../Elements/GeneralEditPanelBase';
import ErrorMessage from '../../../Elements/ErrorMessage';
import SurveyTransferMappingService from '../../../Services/SurveyTransferMappingService';
import SurveyTransferQuestionMappingItem from '../../../Domain/SurveyTransferQuestionMappingItem';
import SurveyTransferQuestionRulesSearchParams from './SurveyTransferQuestionRulesSearchParams';
import SurveyTransferQuestionMappingPanelDescription from './SurveyTransferQuestionMappingPanelDescription';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';

// Page (reached via ?id=<SurveyTransferMappingId>) that lists the question rules of a single
// survey transfer mapping, with server-side filtering/paging. Each row has an action menu
// (Edit + View answer mapping). Editing (via the edit panel) re-points the source question
// (or sets a job-data source).
const SurveyTransferQuestionRulesDetails = () => {
  const surveyTransferMappingId = new URLSearchParams(window.location.search).get('id');

  const [searchItem, setSearchItem] = useState<any>(() => {
    // Skip the URL-param loading of SearchParamsBase (the only url param here is ?id=).
    const sp: any = new (SurveyTransferQuestionRulesSearchParams as any)(true);
    sp.SurveyTransferMappingId = surveyTransferMappingId;
    return sp;
  });
  // The source survey's questions are the same for every row, so fetch them once and reuse
  // them as the dropdown options (mapped to the generic { value, text, secondaryId } shape).
  const [sourceQuestionOptions, setSourceQuestionOptions] = useState<any[]>([]);
  const [editedItem, setEditedItem] = useState<any>(null);
  // The parent mapping (for the source/target survey names shown in the header).
  const [mapping, setMapping] = useState<any>(null);
  // Ref to the table so a save can refresh the CURRENT page (instead of resetting to page 1).
  const tableRef = useRef<any>(null);

  useEffect(() => {
    if (!surveyTransferMappingId) {
      return;
    }
    SurveyTransferMappingService.GetSurveyTransferMappingById(surveyTransferMappingId)
      .then((result: any) => setMapping(result))
      .catch((error: any) => ErrorMessage.SetErrorMessage('STQR05', error));
  }, [surveyTransferMappingId]);

  useEffect(() => {
    if (!surveyTransferMappingId) {
      return;
    }
    SurveyTransferMappingService.GetSourceQuestions(surveyTransferMappingId)
      .then((result: any) =>
        setSourceQuestionOptions(
          (result || []).map((q: any) => ({
            value: q.tpQuestionId,
            text: q.questionText,
            secondaryId: q.thirdPartyQuestionId,
          }))
        )
      )
      .catch((error: any) => ErrorMessage.SetErrorMessage('STQR03', error));
  }, [surveyTransferMappingId]);

  if (!surveyTransferMappingId) {
    ErrorMessage.SetErrorMessage('STQR01');
    return null;
  }

  // Wire the row "Edit" action: attach the (shared) source-question options to the edited
  // item so the panel's SourceQuestionSelect can render them.
  SurveyTransferQuestionMappingItem.editFunction = (id: any, item: any) =>
    setEditedItem({ ...item, SourceQuestionOptions: sourceQuestionOptions });

  // The parent mapping id is not a visible search field, and "Clear All" rebuilds the
  // search params from scratch, so re-inject the scope on every search.
  const startSearch = (newSearchItem: any) => {
    setSearchItem({ ...newSearchItem, SurveyTransferMappingId: surveyTransferMappingId });
  };

  const closeEditPanel = (refresh: any) => {
    setEditedItem(null);
    // GeneralEditPanelBase calls onClose(true) on a successful save; refresh the current page
    // (the table's refresh keeps the current page, unlike bumping searchItem which resets to 1).
    if (refresh === true && tableRef.current) {
      tableRef.current.refresh();
    }
  };

  return (
    <div className='contentwrapper'>
      {mapping && (
        <div className='row m-1'>
          <div className='col-6'>
            <strong>Target Survey:</strong> {mapping.targetSystemName ? `${mapping.targetSystemName} - ` : ''}
            {mapping.targetTpSurveyName || '-'}
          </div>
          <div className='col-6'>
            <strong>Source Survey:</strong> {mapping.sourceSystemName ? `${mapping.sourceSystemName} - ` : ''}
            {mapping.sourceTpSurveyName || '-'}
          </div>
        </div>
      )}
      {editedItem && (
        <GeneralEditPanelBase
          popup={true}
          editedItem={editedItem}
          panelDescription={SurveyTransferQuestionMappingPanelDescription}
          onClose={closeEditPanel}
          maxWidth={1000}
          errorCode='STQR04'
        />
      )}
      <TabOfDataTable
        ref={tableRef}
        type={SurveyTransferQuestionMappingItem}
        serviceFunction={SurveyTransferMappingService.GetQuestionRulesBySearching}
        convertDBItemsFunction={SurveyTransferMappingService.ConvertQuestionRuleDBItems}
        orderBy='TargetTpQuestionDisplayOrder'
        orderDirection={OrderDirectionsEnum.Asc}
        numberOfElementsOnAPage={10}
        searchItem={searchItem}
        searchParamsType={SurveyTransferQuestionRulesSearchParams}
        showSearchStrip={true}
        clickOnStartSearch={startSearch}
        enableUrlPush={false}
        popupEnabled={false}
        errorCode='STQR02'
      />
    </div>
  );
};

export default SurveyTransferQuestionRulesDetails;
